import { z } from 'zod';
/**
 * Contrats de la VISIBILITÉ et de la RÉVOCATION des sessions actives (audit B2).
 *
 * AVANT ce lot, la révocation n'existait que comme EFFET DE BORD : réinitialiser un
 * mot de passe, soft-supprimer un compte, ou révoquer un lien d'accès. Pour
 * déconnecter quelqu'un, le superadmin devait donc lui casser son mot de passe ou
 * supprimer son compte — et il n'avait AUCUN moyen de savoir qui était connecté. Avec
 * le cookie persistant et la session glissante de 30 jours (migration 0039), c'était
 * un angle mort réel.
 *
 * VIE PRIVÉE — ce que le contrat expose, et ce qu'il n'expose PAS :
 *  - JAMAIS le `token_hash` (il vaut le jeton : le divulguer rendrait la session
 *    rejouable par qui lit la page) ;
 *  - AUCUNE adresse IP : `sessions` n'en stocke tout simplement pas (cf. migration
 *    0004) — rien n'est masqué ici, la donnée n'existe pas ;
 *  - `userAgent` EST exposé (colonne `sessions.user_agent`, renseignée à la création,
 *    « indicatif » selon 0004) : c'est le SEUL moyen de distinguer deux sessions du
 *    même compte pour décider laquelle couper.
 */
/**
 * Une session ACTIVE d'un compte (`GET /api/admin/users/:id/sessions`).
 *
 * `sessionLinkId`/`sessionLinkLabel` : le LIEN D'ACCÈS par lequel la session est
 * entrée (`sessions.session_link_id`, migration 0039), ou `null` pour un login par
 * mot de passe — le cas de l'immense majorité des lignes. C'est ce qui rend enfin
 * COMPTABLE la diffusion d'un lien communautaire : on voit combien de sessions un
 * lien donné a ouvertes, et on peut en couper une sans révoquer le lien entier.
 *
 * ⚠ LIMITE HÉRITÉE (documentée par la migration 0039) : les sessions ouvertes AVANT
 * cette migration portent `session_link_id = NULL` même si elles venaient d'un lien —
 * la donnée n'a jamais été enregistrée et aucun remplissage rétroactif n'est possible.
 * Un `null` signifie donc « login par mot de passe OU session antérieure à 0039 ».
 */
export const adminSessionSchema = z.object({
    id: z.uuid(),
    /** En-tête `User-Agent` capturé à la création, ou `null`. Jamais d'IP (non stockée). */
    userAgent: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    /** Dernière activité (« touch » de la garde), ou `null` si jamais réutilisée. */
    lastUsedAt: z.string().datetime({ offset: true }).nullable(),
    expiresAt: z.string().datetime({ offset: true }),
    /** Lien d'accès d'origine, ou `null` (login par mot de passe). */
    sessionLinkId: z.uuid().nullable(),
    /** Libellé du lien d'origine (jointure `session_links.label`), ou `null`. */
    sessionLinkLabel: z.string().nullable(),
    /** La session en cours d'utilisation par le superadmin qui consulte la page. */
    current: z.boolean(),
});
/** Liste des sessions actives d'un compte, la plus récemment utilisée d'abord. */
export const adminSessionListSchema = z.object({
    sessions: z.array(adminSessionSchema),
});
/** Résultat d'une révocation unitaire (`DELETE /api/admin/sessions/:id`). */
export const revokeSessionResultSchema = z.object({
    /** `false` si la session était déjà révoquée/expirée (idempotent, jamais une erreur). */
    revoked: z.boolean(),
});
//# sourceMappingURL=admin-session.js.map